<template>
  <b-modal
    id="moveFolderModal"
    ref="moveFolderModal"
    :title="isDocument ? $lang.archiveDocumentsManagement.moveFolder.titleDocument : $lang.archiveDocumentsManagement.moveFolder.title"
    size="lg"
    centered
    @hide="clear"
  >
    <p v-if="item">
      <strong>{{ isDocument ? $lang.archiveDocumentsManagement.moveFolder.labelDocument : $lang.archiveDocumentsManagement.moveFolder.labelFolder }}:</strong>
      {{ originLabel }}
    </p>
    <p v-if="isDocument" class="text-muted small">
      {{ $lang.archiveDocumentsManagement.moveFolder.documentHint }}
    </p>

    <div class="d-flex align-items-center justify-content-between mb-2">
      <span>
        <strong>{{ $lang.archiveDocumentsManagement.moveFolder.labelDestination }}:</strong>
        {{ destinationLabel }}
      </span>
      <b-button
        size="sm"
        variant="secondary"
        :disabled="path.length === 0"
        @click="goUp"
      >
        <i class="fas fa-level-up-alt" />
        {{ $lang.archiveDocumentsManagement.moveFolder.up }}
      </b-button>
    </div>

    <b-list-group>
      <b-list-group-item v-if="folders.length === 0">
        {{ $lang.archiveDocumentsManagement.moveFolder.empty }}
      </b-list-group-item>
      <b-list-group-item
        v-for="folder in folders"
        :key="folder.id"
        class="d-flex justify-content-between align-items-center"
      >
        <span>
          <i class="fas fa-folder fa-lg text-third" />
          {{ folder.nome }}
        </span>
        <b-button size="sm" variant="third" @click="enter(folder)">
          {{ $lang.archiveDocumentsManagement.moveFolder.open }}
        </b-button>
      </b-list-group-item>
    </b-list-group>

    <template #modal-footer="{ cancel }">
      <b-button variant="danger" @click="cancel()">
        <i class="fas fa-times-circle" />
        {{ $lang.archiveDocumentsManagement.addDocument.cancelTitle }}
      </b-button>
      <b-button variant="third" :disabled="!canMove" @click="onMove">
        <i class="fas fa-check-circle" />
        {{ $lang.archiveDocumentsManagement.moveFolder.okTitle }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import _ from 'lodash'
import { loading, toast } from '@/shared/utils/UIHelper'
import ActionsForm from '@/shared/mixins/actionsForm'

import DocumentsFolderRepository from '@/shared/http/repositories/business/documentsfolder'

const FOLDER_TYPE = 1
const DOCUMENT_TYPE = 2
const SUCCESS_CODE = 200

export default {
  name: 'MoveFolderModal',
  mixins: [
    ActionsForm
  ],
  data () {
    return {
      // Pasta ou documento que esta sendo movido.
      item: null,
      // Caminho dela na arvore, da raiz ate ela (nomes), calculado pela tela.
      originPath: [],
      // Caminho ja navegado dentro do modal. Vazio = raiz.
      path: [],
      // Subpastas do destino atual, ja sem a pasta que esta sendo movida.
      folders: []
    }
  },
  computed: {
    isDocument () {
      return !!this.item && this.item.tipo === DOCUMENT_TYPE
    },
    // Documento precisa de pasta (documento.pastaid e NOT NULL) e mover pra pasta onde ja
    // esta nao faz nada. Pasta aceita qualquer destino listado, inclusive a raiz.
    canMove () {
      if (!this.item) return false
      if (!this.isDocument) return true

      return !!this.destination && this.destination.id !== this.item.pastaID
    },
    // Caminho completo da origem. Sem o caminho (pasta fora da arvore carregada) cai no nome.
    originLabel () {
      if (!this.item) return ''

      return this.originPath.length > 0 ? this.originPath.join(' / ') : this.item.nome
    },
    destination () {
      return this.path.length > 0 ? this.path[this.path.length - 1] : null
    },
    destinationLabel () {
      if (this.path.length === 0) {
        return this.$lang.archiveDocumentsManagement.moveFolder.root
      }

      return this.path.map(folder => folder.nome).join(' / ')
    }
  },
  methods: {
    async show (item, originPath = []) {
      this.item = item
      this.originPath = originPath
      this.path = []

      await this.load()

      this.$refs['moveFolderModal'].show()
    },
    hide () {
      this.$refs['moveFolderModal'].hide()
      this.clear()
    },
    clear () {
      this.item = null
      this.originPath = []
      this.path = []
      this.folders = []
    },
    // Lista as subpastas do destino atual. A pasta que esta sendo movida sai da lista:
    // sem ela nao ha como navegar ate uma descendente, entao o destino oferecido nunca
    // gera ciclo. O backend valida de novo, isso aqui e so pra nao oferecer o invalido.
    async load () {
      loading.push()

      try {
        const response = this.destination
          ? await DocumentsFolderRepository.GetAllInFolder(this.CompanySelected, this.destination.id)
          : await DocumentsFolderRepository.GetAll(this.CompanySelected)

        if (response.data.code !== SUCCESS_CODE) {
          toast.error(response.data.message)
          this.folders = []
          return
        }

        this.folders = (response.data.data || [])
          .filter(conteudo => conteudo.tipo === FOLDER_TYPE)
          .filter(conteudo => !this.item || conteudo.id !== this.item.id)
      } catch (error) {
        this.onException(error)
      } finally {
        loading.pop()
      }
    },
    async enter (folder) {
      this.path.push(_.cloneDeep(folder))
      await this.load()
    },
    async goUp () {
      this.path.pop()
      await this.load()
    },
    onMove () {
      loading.push()

      const destinationId = this.destination ? this.destination.id : null

      const request = this.isDocument
        ? DocumentsFolderRepository.MoveDocument(this.CompanySelected, this.item.id, destinationId)
        : DocumentsFolderRepository.MoveFolder(this.CompanySelected, this.item.id, destinationId)

      request
        .then((response) => {
          // code === 200 e nao code >= 0: SetNotFound responde 204 e as telas irmas deste
          // modulo tratam isso como sucesso — recusa do backend virava toast verde.
          if (response.data.code === SUCCESS_CODE) {
            toast.success(this.$lang.Common.Alerts.Success)
            this.emitMoved()
          } else {
            toast.error(response.data.message)
          }

          this.$nextTick(() => {
            loading.close()
            this.hide()
          })
        })
        .catch(this.onException)
    },
    emitMoved () {
      this.$emit('moved', {})
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/private/archiveDocumentsManagement/components/MoveFolder/MoveFolderModal.vue